// VK Mini App — клиент для работы с прокси-API
// Вместо telegram_id используется vk_id, но на бэкенде поле называется telegram_id

const PROXY_URL = process.env.REACT_APP_PROXY_URL || 'https://api.testagentn8n.ru';

interface ProxyResponse<T> {
  data: T | null;
  error: string | null;
}

const log = (method: string, url: string, data?: any) => {
  console.log(`🔵 [VK Proxy] ${method} ${url}`);
  if (data) console.log('   Data:', data);
};

const logError = (method: string, url: string, error: any) => {
  console.error(`🔴 [VK Proxy] ${method} ${url} FAILED`);
  console.error('   Error:', error);
};

const logSuccess = (method: string, url: string, result: any) => {
  console.log(`🟢 [VK Proxy] ${method} ${url} SUCCESS`);
  console.log('   Result:', result ? JSON.stringify(result).slice(0, 150) : 'null');
};

// Получить доску
export const getBoard = async (boardId: string) => {
  const url = `${PROXY_URL}/api/boards/${boardId}`;
  log('GET', url);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result: ProxyResponse<any> = await response.json();
    if (result.error) throw new Error(result.error);
    logSuccess('GET', url, result.data);
    return result.data;
  } catch (error) {
    logError('GET', url, error);
    throw error;
  }
};

// Получить выборы позиций
export const getItemSelections = async (boardId: string) => {
  const url = `${PROXY_URL}/api/boards/${boardId}/selections`;
  log('GET', url);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result: ProxyResponse<any[]> = await response.json();
    if (result.error) throw new Error(result.error);
    logSuccess('GET', url, result.data);
    return result.data;
  } catch (error) {
    logError('GET', url, error);
    throw error;
  }
};

// Создать выбор позиции
export const createItemSelection = async (selection: {
  item_id: string;
  participant_id: string;
  board_id: string;
}) => {
  const url = `${PROXY_URL}/api/item-selections`;
  log('POST', url, selection);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selection)
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result: ProxyResponse<any> = await response.json();
    if (result.error) throw new Error(result.error);
    logSuccess('POST', url, result.data);
    return result.data;
  } catch (error) {
    logError('POST', url, error);
    throw error;
  }
};

// Удалить выбор позиции
export const deleteItemSelection = async (id: string) => {
  const url = `${PROXY_URL}/api/item-selections/${id}`;
  log('DELETE', url);
  try {
    const response = await fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result: ProxyResponse<null> = await response.json();
    if (result.error) throw new Error(result.error);
    logSuccess('DELETE', url, null);
  } catch (error) {
    logError('DELETE', url, error);
    throw error;
  }
};

// Обновить выбор позиции
export const updateItemSelection = async (id: string, updates: Record<string, unknown>) => {
  const response = await fetch(`${PROXY_URL}/api/item-selections/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  const result: ProxyResponse<any> = await response.json();
  if (result.error) throw new Error(result.error);
  return result.data;
};

// Добавить участника (vk_id передаётся как telegram_id для совместимости с бэкендом)
export const addParticipant = async (boardId: string, profile: {
  telegram_id: number; // VK user ID
  username?: string;
  first_name?: string;
  last_name?: string;
}) => {
  const url = `${PROXY_URL}/api/participants`;
  log('POST', url, { boardId, profile });
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ boardId, profile })
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result: ProxyResponse<any> = await response.json();
    if (result.error) throw new Error(result.error);
    logSuccess('POST', url, result.data);
    return result.data;
  } catch (error) {
    logError('POST', url, error);
    throw error;
  }
};

// Добавить участника по имени
export const addParticipantByName = async (boardId: string, name: string, vkId?: number) => {
  const profile = {
    telegram_id: vkId || Math.floor(Math.random() * 1000000),
    first_name: name,
    username: '',
    last_name: ''
  };
  return addParticipant(boardId, profile);
};

// Обновить доску
export const updateBoard = async (boardId: string, updates: {
  restaurant_name?: string;
  address?: string;
  total_amount?: number;
}, vkId?: number) => {
  const response = await fetch(`${PROXY_URL}/api/boards/${boardId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates, telegramId: vkId }) // бэкенд ожидает telegramId
  });
  const result: ProxyResponse<any> = await response.json();
  if (result.error) throw new Error(result.error);
  return result.data;
};

// Массовое обновление позиций
export const updateBoardItems = async (boardId: string, items: Array<{
  id?: string;
  name: string;
  price: number;
  quantity: number;
  emoji?: string;
  isNew?: boolean;
}>, vkId?: number) => {
  const response = await fetch(`${PROXY_URL}/api/boards/${boardId}/items/bulk-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, telegramId: vkId })
  });
  const result: ProxyResponse<any> = await response.json();
  if (result.error) throw new Error(result.error);
  return result.data;
};

// Удалить позицию
export const deleteBoardItem = async (itemId: string, vkId?: number) => {
  const url = vkId
    ? `${PROXY_URL}/api/items/${itemId}?telegramId=${vkId}`
    : `${PROXY_URL}/api/items/${itemId}`;

  const response = await fetch(url, { method: 'DELETE' });
  const result: ProxyResponse<null> = await response.json();
  if (result.error) throw new Error(result.error);
};

// Получить доски пользователя
export const getUserBoards = async (vkId: number) => {
  const url = `${PROXY_URL}/api/user/${vkId}/boards`;
  log('GET', url);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result: ProxyResponse<any[]> = await response.json();
    if (result.error) throw new Error(result.error);
    logSuccess('GET', url, result.data);
    return result.data;
  } catch (error) {
    logError('GET', url, error);
    throw error;
  }
};
